import { useParams, useNavigate } from 'react-router-dom';
import { useAdminUserProfileDetails, useAdminUserActivityTimeseries, useAdminUpdateUserProfile } from '@/hooks/useAdminUsers';
import {
    useAdminUserCaptures,
    useAdminDeleteCapture,
    useAdminUpdateCapture,
    useAdminPromoteCapture,
    useAdminBanUser,
    useAdminUnbanUser,
    useAdminPromoteToAdmin,
    useAdminDemoteFromAdmin,
    useAdminCaptureComments,
    useAdminUpdateCaptureComment,
    type AdminCapture,
    type AdminCaptureComment,
} from '@/hooks/useAdminUserCaptures';
import { PageHeader } from '@/components/shared/PageHeader';
import { SimpleCapturesAdminCard } from '@/components/SimpleCapturesAdminCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';
import {
    ArrowLeft, Calendar, MapPin, Mail, Shield, ShieldCheck, ShieldOff, Zap, Target,
    Trophy, GraduationCap, Users, Video, Clock, Activity,
    CreditCard, Coins, Smartphone, Share2, Fish, Trash2, Globe, GlobeLock,
    Star, Edit2, MessageSquare, X, Loader2, ClipboardList,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    Legend
} from 'recharts';
import { cn } from '@/lib/utils';
import { isAppVersionLatest } from '@/lib/compareVersions';
import { useState, useCallback, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { profileEligibleForWelcomeV2 } from '@/constants/onboardingAdmin';

type UserAdminConfirm =
    | null
    | { kind: 'ban' }
    | { kind: 'unban' }
    | { kind: 'promoteAdmin' }
    | { kind: 'demoteAdmin' }
    | { kind: 'deleteCapture'; captureId: string };

/** CDN de Azure Front Door para media (mismo que la app principal) */
const MEDIA_BASE = 'https://aipeces-media-a4hsbvf5gff3b6b0.z02.azurefd.net';

export function UserDetailPage() {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const { data: user, isLoading } = useAdminUserProfileDetails(userId || null);
    const { data: activityData, isLoading: isLoadingActivity } = useAdminUserActivityTimeseries(userId || null, 30);
    const { data: captures, isLoading: capturesLoading } = useAdminUserCaptures(userId || null);
    const [activeTab, setActiveTab] = useState('overview');
    const [expandedCaptureId, setExpandedCaptureId] = useState<string | null>(null);
    const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
    const [editCommentText, setEditCommentText] = useState('');
    const [editUserOpen, setEditUserOpen] = useState(false);
    const [editUserForm, setEditUserForm] = useState<{
        display_name: string;
        avatar_url: string;
        country_code: string;
        country_name: string;
        region: string;
        onboarding_completed: boolean;
        profile_onboarding_ack_json: string;
    }>({
        display_name: '',
        avatar_url: '',
        country_code: '',
        country_name: '',
        region: '',
        onboarding_completed: false,
        profile_onboarding_ack_json: '{}',
    });
    /** Solo enviar onboarding/JSON a RPC si el admin los modificó (evita false accidental al guardar nombre). */
    const editUserSnapshotRef = useRef<{
        onboarding_completed: boolean;
        profile_onboarding_ack_json: string;
    } | null>(null);
    const [confirmAction, setConfirmAction] = useState<UserAdminConfirm>(null);
    const [alertMessage, setAlertMessage] = useState<string | null>(null);

    const deleteCapture = useAdminDeleteCapture(userId || '');
    const updateCapture = useAdminUpdateCapture(userId || '');
    const promoteCapture = useAdminPromoteCapture(userId || '');
    const banUser = useAdminBanUser(userId || '');
    const unbanUser = useAdminUnbanUser(userId || '');
    const promoteToAdmin = useAdminPromoteToAdmin(userId || '');
    const demoteFromAdmin = useAdminDemoteFromAdmin(userId || '');
    const updateComment = useAdminUpdateCaptureComment(userId || '');
    const updateUserProfile = useAdminUpdateUserProfile(userId || null);
    const { user: currentUser } = useAuth();

    const runConfirmedAction = useCallback(
        async (action: Exclude<UserAdminConfirm, null>) => {
            try {
                switch (action.kind) {
                    case 'ban':
                        await banUser.mutateAsync();
                        break;
                    case 'unban':
                        await unbanUser.mutateAsync();
                        break;
                    case 'promoteAdmin':
                        await promoteToAdmin.mutateAsync();
                        break;
                    case 'demoteAdmin':
                        await demoteFromAdmin.mutateAsync();
                        break;
                    case 'deleteCapture':
                        await deleteCapture.mutateAsync(action.captureId);
                        break;
                }
            } catch (e: unknown) {
                const msg =
                    e && typeof e === 'object' && 'message' in e
                        ? String((e as { message?: string }).message)
                        : 'Error';
                setAlertMessage(msg);
            }
        },
        [banUser, unbanUser, promoteToAdmin, demoteFromAdmin, deleteCapture]
    );

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="p-6 text-center">
                <h2 className="text-xl font-semibold mb-2">Usuario no encontrado</h2>
                <button
                    onClick={() => navigate('/users')}
                    className="text-primary hover:underline"
                >
                    Volver a la lista
                </button>
            </div>
        );
    }

    const {
        profile,
        subscription,
        gamification,
        token_balance,
        stats,
        recent_activity,
        app_version: userAppVersion,
        recommended_version: recommendedAppVersion,
    } = user;
    const appVersionIsLatest = isAppVersionLatest(
        userAppVersion?.app_version,
        recommendedAppVersion?.version
    );
    const isBanned = !!profile.banned_at;
    const isAdmin = !!profile.is_admin;
    const isCurrentUser = !!currentUser?.id && !!profile.user_id && currentUser.id === profile.user_id;

    const formatDate = (dateString?: string | null) => {
        if (!dateString) return 'N/A';
        try {
            return format(new Date(dateString), 'd MMM yyyy, HH:mm', { locale: es });
        } catch (e) {
            return dateString;
        }
    };

    const handleBan = () => setConfirmAction({ kind: 'ban' });
    const handleUnban = () => setConfirmAction({ kind: 'unban' });
    const handlePromoteToAdmin = () => setConfirmAction({ kind: 'promoteAdmin' });
    const handleDemoteFromAdmin = () => setConfirmAction({ kind: 'demoteAdmin' });

    const handleConfirmDialog = () => {
        setConfirmAction((act) => {
            if (!act) return null;
            void runConfirmedAction(act);
            return null;
        });
    };

    const handleOpenEditUser = () => {
        const ack =
            profile.profile_onboarding_ack && typeof profile.profile_onboarding_ack === 'object'
                ? profile.profile_onboarding_ack
                : {};
        let ackJson = '{}';
        try {
            ackJson = JSON.stringify(ack, null, 2);
        } catch {
            ackJson = '{}';
        }
        setEditUserForm({
            display_name: profile.display_name ?? '',
            avatar_url: profile.avatar_url ?? '',
            country_code: profile.country_code ?? '',
            country_name: profile.country_name ?? '',
            region: profile.region ?? '',
            onboarding_completed: Boolean(profile.onboarding_completed),
            profile_onboarding_ack_json: ackJson,
        });
        editUserSnapshotRef.current = {
            onboarding_completed: Boolean(profile.onboarding_completed),
            profile_onboarding_ack_json: ackJson,
        };
        setEditUserOpen(true);
    };

    const handleSaveEditUser = async () => {
        let ackObj: Record<string, unknown>;
        try {
            const raw = editUserForm.profile_onboarding_ack_json.trim() || '{}';
            const parsed = JSON.parse(raw);
            ackObj =
                parsed && typeof parsed === 'object' && !Array.isArray(parsed)
                    ? (parsed as Record<string, unknown>)
                    : {};
        } catch {
            setAlertMessage(
                'El campo «Checklist perfil (JSON)» no es un objeto JSON válido. Revisa llaves y comillas.'
            );
            return;
        }
        try {
            const snap = editUserSnapshotRef.current;
            const onboardingChanged =
                snap !== null &&
                editUserForm.onboarding_completed !== snap.onboarding_completed;
            const ackTrim = editUserForm.profile_onboarding_ack_json.trim();
            const ackChanged =
                snap !== null &&
                ackTrim !== snap.profile_onboarding_ack_json.trim();

            await updateUserProfile.mutateAsync({
                display_name: editUserForm.display_name || null,
                avatar_url: editUserForm.avatar_url || null,
                country_code: editUserForm.country_code || null,
                country_name: editUserForm.country_name || null,
                region: editUserForm.region || null,
                ...(onboardingChanged ? { onboarding_completed: editUserForm.onboarding_completed } : {}),
                ...(ackChanged ? { profile_onboarding_ack: ackObj } : {}),
            });
            setEditUserOpen(false);
        } catch (e: unknown) {
            const msg =
                e && typeof e === 'object' && 'message' in e
                    ? String((e as { message?: string }).message)
                    : 'Error al guardar perfil';
            setAlertMessage(msg);
        }
    };

    const handleDeleteCaptureRequest = (captureId: string) => {
        setConfirmAction({ kind: 'deleteCapture', captureId });
    };

    const handleTogglePublic = async (c: AdminCapture) => {
        try {
            await updateCapture.mutateAsync({ captureId: c.id, is_public: !c.is_public });
        } catch (e: unknown) {
            const msg =
                e && typeof e === 'object' && 'message' in e
                    ? String((e as { message?: string }).message)
                    : 'Error al actualizar';
            setAlertMessage(msg);
        }
    };

    const handlePromote = async (captureId: string) => {
        try {
            await promoteCapture.mutateAsync(captureId);
        } catch (e: unknown) {
            const msg =
                e && typeof e === 'object' && 'message' in e
                    ? String((e as { message?: string }).message)
                    : 'Error al promocionar';
            setAlertMessage(msg);
        }
    };

    const handleSaveComment = async () => {
        if (!editingCommentId || !editCommentText.trim()) return;
        try {
            await updateComment.mutateAsync({ commentId: editingCommentId, text: editCommentText.trim() });
            setEditingCommentId(null);
            setEditCommentText('');
        } catch (e: unknown) {
            const msg =
                e && typeof e === 'object' && 'message' in e
                    ? String((e as { message?: string }).message)
                    : 'Error al guardar comentario';
            setAlertMessage(msg);
        }
    };

    return (
        <div className="space-y-6 animate-fade-in pb-10">
            {/* Header */}
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div>
                    <button
                        onClick={() => navigate('/users')}
                        className="flex items-center text-sm text-muted-foreground hover:text-foreground mb-2 transition-colors"
                    >
                        <ArrowLeft size={16} className="mr-1" /> Volver a Usuarios
                    </button>
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center overflow-hidden border-2 border-border">
                            {profile.avatar_url ? (
                                <img src={profile.avatar_url} alt={profile.display_name || 'User'} className="w-full h-full object-cover" />
                            ) : (
                                <span className="text-2xl font-bold text-muted-foreground">
                                    {(profile.display_name || profile.email || '?')[0].toUpperCase()}
                                </span>
                            )}
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                                {profile.display_name || 'Sin nombre'}
                                {isBanned && (
                                    <StatusBadge status="BANEADO" variant="error" />
                                )}
                                {subscription && !isBanned && (
                                    <StatusBadge
                                        status={subscription.plan_id.toUpperCase()}
                                        variant={subscription.plan_id === 'premium' ? 'warning' : subscription.plan_id === 'pro' ? 'info' : 'neutral'}
                                    />
                                )}
                                {isAdmin && (
                                    <StatusBadge status="ADMIN" variant="info" />
                                )}
                            </h1>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1">
                                <span className="flex items-center gap-1">
                                    <Mail size={14} /> {profile.email}
                                </span>
                                {profile.country_name && (
                                    <span className="flex items-center gap-1">
                                        <MapPin size={14} /> {profile.country_name}
                                    </span>
                                )}
                                <span className="flex items-center gap-1">
                                    <Calendar size={14} /> Reg: {formatDate(profile.created_at)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex gap-2 flex-wrap">
                    <button
                        onClick={handleOpenEditUser}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium flex items-center gap-2"
                    >
                        <Edit2 size={16} />
                        Editar usuario
                    </button>
                    {isBanned ? (
                        <button
                            onClick={handleUnban}
                            disabled={unbanUser.isPending}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50"
                        >
                            Desbanear
                        </button>
                    ) : (
                        <button
                            onClick={handleBan}
                            disabled={banUser.isPending}
                            className="px-4 py-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 transition-colors text-sm font-medium disabled:opacity-50"
                        >
                            Banear usuario
                        </button>
                    )}
                    {isAdmin ? (
                        <button
                            onClick={handleDemoteFromAdmin}
                            disabled={demoteFromAdmin.isPending || isCurrentUser}
                            title={isCurrentUser ? 'No puedes quitarte permisos de admin a ti mismo' : undefined}
                            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                        >
                            <ShieldOff size={16} />
                            Quitar admin
                        </button>
                    ) : (
                        <button
                            onClick={handlePromoteToAdmin}
                            disabled={promoteToAdmin.isPending}
                            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                        >
                            <ShieldCheck size={16} />
                            Dar permisos admin
                        </button>
                    )}
                </div>
            </div>

            {/* KPI Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard
                    icon={<Target className="text-blue-500" />}
                    label="Capturas"
                    value={stats.total_captures}
                    subvalue={`${stats.unique_species} especies`}
                />
                <KpiCard
                    icon={<Shield className="text-red-500" />}
                    label="Batallas"
                    value={stats.total_battles}
                    subvalue={`${((stats.battles_won / (stats.total_battles || 1)) * 100).toFixed(0)}% victoria`}
                />
                <KpiCard
                    icon={<GraduationCap className="text-purple-500" />}
                    label="Quizzes"
                    value={stats.total_quizzes}
                    subvalue={`${stats.avg_quiz_score.toFixed(1)} avg`}
                />
                <KpiCard
                    icon={<Coins className="text-yellow-500" />}
                    label="Tokens"
                    value={token_balance?.current_tokens || 0}
                    subvalue={`${token_balance?.tokens_used_this_period || 0} usados`}
                />
            </div>

            {userId ? <SimpleCapturesAdminCard userId={userId} /> : null}

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5 shadow-sm">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <Activity size={18} className="text-muted-foreground" />
                        Actividad (Últimos 30 días)
                    </h3>
                    <div className="h-[300px] w-full">
                        {isLoadingActivity ? (
                            <div className="flex items-center justify-center h-full text-muted-foreground">Cargando gráfica...</div>
                        ) : activityData && activityData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={activityData}>
                                    <defs>
                                        <linearGradient id="colorCaptures" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                        </linearGradient>
                                        <linearGradient id="colorBattles" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                                        </linearGradient>
                                        <linearGradient id="colorQuizzes" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#a855f7" stopOpacity={0.2} />
                                            <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                                    <XAxis
                                        dataKey="date"
                                        tickFormatter={(val) => format(new Date(val), 'd MMM', { locale: es })}
                                        fontSize={12}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <YAxis yAxisId="left" orientation="left" fontSize={12} tickLine={false} axisLine={false} />
                                    <YAxis yAxisId="right" orientation="right" fontSize={12} tickLine={false} axisLine={false} />
                                    <Tooltip
                                        contentStyle={{ backgroundColor: 'var(--popover)', borderRadius: '8px', border: '1px solid var(--border)' }}
                                        labelFormatter={(val) => format(new Date(val), 'd MMMM yyyy', { locale: es })}
                                    />
                                    <Legend iconType="circle" />
                                    <Area yAxisId="left" type="monotone" dataKey="captures_count" name="Capturas" stroke="#3b82f6" fillOpacity={1} fill="url(#colorCaptures)" strokeWidth={2} />
                                    <Area yAxisId="right" type="monotone" dataKey="battles_count" name="Batallas" stroke="#ef4444" fillOpacity={1} fill="url(#colorBattles)" strokeWidth={2} />
                                    <Area yAxisId="right" type="monotone" dataKey="quizzes_count" name="Quizzes" stroke="#a855f7" fillOpacity={0.3} fill="url(#colorQuizzes)" strokeWidth={2} strokeDasharray="5 5" />
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="flex items-center justify-center h-full text-muted-foreground">Sin datos de actividad reciente</div>
                        )}
                    </div>
                </div>

                <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <Trophy size={18} className="text-muted-foreground" />
                        Estadísticas de Juego
                    </h3>
                    {gamification ? (
                        <div className="space-y-6">
                            <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                                <div>
                                    <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Nivel Actual</span>
                                    <div className="text-3xl font-bold text-primary">{gamification.current_level}</div>
                                </div>
                                <div className="text-right">
                                    <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Energía</span>
                                    <div className="text-lg font-medium">
                                        {gamification.current_energy} <span className="text-muted-foreground text-sm">/ {gamification.max_energy}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-3">
                                <h4 className="text-sm font-medium text-muted-foreground mb-2">Atributos del Avatar</h4>
                                <StatBar label="Poder" value={gamification.avatar_stats?.power || 0} color="bg-red-500" />
                                <StatBar label="Velocidad" value={gamification.avatar_stats?.speed || 0} color="bg-yellow-500" />
                                <StatBar label="Defensa" value={gamification.avatar_stats?.defense || 0} color="bg-blue-500" />
                            </div>
                            <div className="pt-2 border-t border-border mt-2">
                                <div className="grid grid-cols-2 gap-2 text-center">
                                    <div className="p-2">
                                        <div className="text-2xl font-bold text-green-500">{gamification.victories}</div>
                                        <div className="text-xs text-muted-foreground">Victorias</div>
                                    </div>
                                    <div className="p-2">
                                        <div className="text-2xl font-bold text-red-500">{gamification.defeats}</div>
                                        <div className="text-xs text-muted-foreground">Derrotas</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-8 text-muted-foreground">Usuario sin datos de juego</div>
                    )}
                </div>
            </div>

            {/* Details Tabs */}
            <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
                <div className="flex bg-muted/30 border-b border-border">
                    <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} label="Visión General" icon={<Activity size={16} />} />
                    <TabButton active={activeTab === 'subscription'} onClick={() => setActiveTab('subscription')} label="Suscripción" icon={<CreditCard size={16} />} />
                    <TabButton active={activeTab === 'captures'} onClick={() => setActiveTab('captures')} label="Capturas" icon={<Fish size={16} />} />
                </div>

                <div className="p-6">
                    {activeTab === 'overview' && (
                        <div className="space-y-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <h3 className="text-base font-semibold mb-4 flex items-center gap-2">
                                        <Clock size={18} className="text-muted-foreground" />
                                        Actividad Reciente
                                    </h3>
                                    <div className="space-y-4">
                                        {recent_activity && recent_activity.length > 0 ? (
                                            recent_activity.map((event, i) => (
                                                <div key={i} className="flex gap-3 items-start pb-4 border-b border-border last:border-0 last:pb-0">
                                                    <div className="mt-1 p-1.5 rounded-full bg-muted text-muted-foreground">
                                                        <ActivityIcon type={event.event_type} />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-sm text-foreground">{formatEventType(event.event_type)}</p>
                                                        <p className="text-xs text-muted-foreground">{formatDate(event.created_at)}</p>
                                                        {event.metadata && Object.keys(event.metadata).length > 0 && (
                                                            <div className="text-xs text-muted-foreground/80 mt-1 font-mono bg-muted/30 p-1.5 rounded">
                                                                {JSON.stringify(event.metadata).slice(0, 100)}
                                                                {JSON.stringify(event.metadata).length > 100 ? '...' : ''}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-sm text-muted-foreground italic">Sin actividad registrada recientemente</p>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-base font-semibold mb-4 flex items-center gap-2">
                                        <Smartphone size={18} className="text-muted-foreground" />
                                        Información Adicional
                                    </h3>
                                    <div className="space-y-3">
                                        <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-2">
                                            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                                                <ClipboardList size={16} className="text-primary shrink-0" />
                                                Onboarding y primera configuración
                                            </div>
                                            <p className="text-xs text-muted-foreground leading-snug">
                                                Si «Onboarding servidor» es no, la app bloquea publicar capturas hasta
                                                completarlo. El onboarding pantalla completa (V2) solo se muestra a
                                                cuentas registradas desde abril de 2026; el estado en servidor se puede
                                                corregir aquí como admin.
                                            </p>
                                            <DetailRow
                                                label="Onboarding servidor"
                                                value={
                                                    profile.onboarding_completed === true
                                                        ? 'Completo'
                                                        : 'Pendiente (bloqueo al publicar capturas)'
                                                }
                                            />
                                            <DetailRow
                                                label="Registro onboarding V2 (desde abril 2026)"
                                                value={
                                                    profileEligibleForWelcomeV2(profile.created_at) ? 'Sí' : 'No'
                                                }
                                            />
                                            <div className="border-t border-border pt-2">
                                                <p className="text-xs font-medium text-muted-foreground mb-1">
                                                    Checklist «Completa tu perfil» (servidor / JSON)
                                                </p>
                                                <pre className="text-[11px] font-mono bg-background/80 rounded-md p-2 overflow-x-auto max-h-32 whitespace-pre-wrap break-all">
                                                    {(() => {
                                                        try {
                                                            return JSON.stringify(
                                                                profile.profile_onboarding_ack ?? {},
                                                                null,
                                                                2
                                                            );
                                                        } catch {
                                                            return '{}';
                                                        }
                                                    })()}
                                                </pre>
                                            </div>
                                        </div>
                                        <DetailRow label="ID de Usuario" value={profile.user_id} copyable />
                                        <DetailRow label="Plataforma" value={profile.platform || 'Desconocida'} />
                                        <div className="py-2 border-b border-muted/50 space-y-1.5">
                                            <div className="flex justify-between items-start gap-3">
                                                <span className="text-sm text-muted-foreground font-medium shrink-0">
                                                    Versión de la App
                                                </span>
                                                <div className="flex flex-col items-end gap-1.5 min-w-0">
                                                    {userAppVersion?.app_version ? (
                                                        <>
                                                            <span className="text-sm text-foreground font-mono">
                                                                {userAppVersion.app_version}
                                                            </span>
                                                            {appVersionIsLatest === true && (
                                                                <StatusBadge status="Última" variant="success" size="sm" />
                                                            )}
                                                            {appVersionIsLatest === false && (
                                                                <StatusBadge
                                                                    status="No es la última"
                                                                    variant="warning"
                                                                    size="sm"
                                                                />
                                                            )}
                                                            {appVersionIsLatest === null && recommendedAppVersion?.version == null && (
                                                                <span className="text-xs text-muted-foreground">
                                                                    Sin versión recomendada configurada
                                                                </span>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <span className="text-sm text-muted-foreground italic">
                                                            Sin datos (aún no reportada)
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {userAppVersion?.app_version && recommendedAppVersion?.version && (
                                                <p className="text-xs text-muted-foreground text-right">
                                                    Recomendada: {recommendedAppVersion.version}
                                                    {userAppVersion.last_seen
                                                        ? ` · Vista ${formatDate(userAppVersion.last_seen)}`
                                                        : ''}
                                                </p>
                                            )}
                                            {userAppVersion?.app_version && !recommendedAppVersion?.version && userAppVersion.last_seen && (
                                                <p className="text-xs text-muted-foreground text-right">
                                                    Vista {formatDate(userAppVersion.last_seen)}
                                                </p>
                                            )}
                                        </div>
                                        <DetailRow label="Videos IA Generados" value={stats.ai_videos_generated.toString()} />
                                        <div className="border-t border-border my-2"></div>
                                        <DetailRow label="Seguidores" value={stats.total_followers.toString()} />
                                        <DetailRow label="Siguiendo" value={stats.total_following.toString()} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'subscription' && (
                        <div>
                            <h3 className="text-lg font-semibold mb-6">Detalles de la Suscripción</h3>
                            {subscription ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-4">
                                        <div className="p-4 bg-muted/20 rounded-lg border border-border">
                                            <p className="text-sm text-muted-foreground uppercase tracking-wider font-semibold mb-1">Plan Actual</p>
                                            <div className="flex items-center gap-3">
                                                <span className="text-2xl font-bold text-primary capitalize">{subscription.plan_id}</span>
                                                <StatusBadge status={subscription.status} variant={subscription.status === 'active' ? 'success' : 'neutral'} />
                                            </div>
                                            {subscription.cancel_at_period_end && (
                                                <p className="text-xs text-yellow-600 mt-2 flex items-center gap-1">
                                                    <Clock size={12} /> Se cancela al final del período
                                                </p>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            <DetailRow label="Stripe Customer ID" value={subscription.stripe_customer_id || 'N/A'} copyable />
                                            <DetailRow label="Subscription ID" value={subscription.stripe_subscription_id || 'N/A'} copyable />
                                            <DetailRow label="Fin del período" value={formatDate(subscription.current_period_end)} />
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <h4 className="font-medium text-foreground">Balance de Tokens</h4>
                                        <div className="p-4 bg-muted/20 rounded-lg border border-border flex items-center justify-between">
                                            <div>
                                                <p className="text-sm text-muted-foreground">Tokens Disponibles</p>
                                                <p className="text-3xl font-bold text-yellow-500 flex items-center gap-2">
                                                    <Coins className="fill-yellow-500" />
                                                    {token_balance?.current_tokens || 0}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm text-muted-foreground">Usados este mes</p>
                                                <p className="text-xl font-semibold">{token_balance?.tokens_used_this_period || 0}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-muted-foreground">Este usuario no tiene datos de suscripción detallados (probablemente plan gratuito básico).</p>
                            )}
                        </div>
                    )}

                    {activeTab === 'captures' && (
                        <CapturesTab
                            captures={captures ?? []}
                            capturesLoading={capturesLoading}
                            expandedCaptureId={expandedCaptureId}
                            setExpandedCaptureId={setExpandedCaptureId}
                            editingCommentId={editingCommentId}
                            setEditingCommentId={setEditingCommentId}
                            editCommentText={editCommentText}
                            setEditCommentText={setEditCommentText}
                            onDelete={handleDeleteCaptureRequest}
                            onTogglePublic={handleTogglePublic}
                            onPromote={handlePromote}
                            onSaveComment={handleSaveComment}
                            updateComment={updateComment}
                            formatDate={formatDate}
                        />
                    )}
                </div>
            </div>

            {/* Modal Editar usuario */}
            {editUserOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-10" onClick={() => setEditUserOpen(false)}>
                    <div className="w-full max-w-xl mx-4 rounded-xl border border-border bg-card p-6 space-y-4 shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-foreground">Editar usuario</h3>
                            <button onClick={() => setEditUserOpen(false)} className="text-muted-foreground hover:text-foreground p-1 rounded">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Nombre para mostrar</label>
                                <input
                                    type="text"
                                    value={editUserForm.display_name}
                                    onChange={e => setEditUserForm(f => ({ ...f, display_name: e.target.value }))}
                                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                                    placeholder="Nombre de usuario"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">URL del avatar</label>
                                <input
                                    type="url"
                                    value={editUserForm.avatar_url}
                                    onChange={e => setEditUserForm(f => ({ ...f, avatar_url: e.target.value }))}
                                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                                    placeholder="https://..."
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Código de país</label>
                                <input
                                    type="text"
                                    value={editUserForm.country_code}
                                    onChange={e => setEditUserForm(f => ({ ...f, country_code: e.target.value }))}
                                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                                    placeholder="ES"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Nombre del país</label>
                                <input
                                    type="text"
                                    value={editUserForm.country_name}
                                    onChange={e => setEditUserForm(f => ({ ...f, country_name: e.target.value }))}
                                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                                    placeholder="España"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Región</label>
                                <input
                                    type="text"
                                    value={editUserForm.region}
                                    onChange={e => setEditUserForm(f => ({ ...f, region: e.target.value }))}
                                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                                    placeholder="Comunidad Valenciana"
                                />
                            </div>
                            <div className="rounded-lg border border-border bg-muted/15 p-3 space-y-3">
                                <p className="text-sm font-medium text-foreground">Onboarding (servidor)</p>
                                <label className="flex items-start gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="mt-1 rounded border-border"
                                        checked={editUserForm.onboarding_completed}
                                        onChange={e =>
                                            setEditUserForm(f => ({
                                                ...f,
                                                onboarding_completed: e.target.checked,
                                            }))
                                        }
                                    />
                                    <span className="text-sm text-foreground leading-snug">
                                        Marcar onboarding inicial como{' '}
                                        <strong className="text-emerald-600">completado</strong>. Desactivar solo si debe
                                        volver el bloqueo (p. ej. pruebas o incidencia).
                                    </span>
                                </label>
                                <div>
                                    <label className="block text-xs font-medium text-muted-foreground mb-1">
                                        Checklist «Completa tu perfil» — JSON (opcional para soporte avanzado)
                                    </label>
                                    <textarea
                                        value={editUserForm.profile_onboarding_ack_json}
                                        onChange={e =>
                                            setEditUserForm(f => ({
                                                ...f,
                                                profile_onboarding_ack_json: e.target.value,
                                            }))
                                        }
                                        rows={6}
                                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground font-mono text-xs"
                                        placeholder="{}"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2 justify-end pt-2">
                            <button
                                onClick={() => setEditUserOpen(false)}
                                className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveEditUser}
                                disabled={updateUserProfile.isPending}
                                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                            >
                                {updateUserProfile.isPending ? <Loader2 size={16} className="animate-spin" /> : <Edit2 size={16} />}
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <AdminConfirmDialog
                open={!!confirmAction}
                variant={
                    confirmAction?.kind === 'ban' || confirmAction?.kind === 'deleteCapture' ? 'danger' : 'default'
                }
                title={
                    confirmAction?.kind === 'ban'
                        ? 'Banear usuario'
                        : confirmAction?.kind === 'unban'
                          ? 'Levantar ban'
                          : confirmAction?.kind === 'promoteAdmin'
                            ? 'Permisos de administrador'
                            : confirmAction?.kind === 'demoteAdmin'
                              ? 'Quitar administrador'
                              : confirmAction?.kind === 'deleteCapture'
                                ? 'Eliminar captura'
                                : ''
                }
                description={
                    confirmAction?.kind === 'ban' ? (
                        <p>¿Banear a este usuario? No podrá acceder a la aplicación.</p>
                    ) : confirmAction?.kind === 'unban' ? (
                        <p>¿Levantar el ban de este usuario?</p>
                    ) : confirmAction?.kind === 'promoteAdmin' ? (
                        <p>
                            ¿Dar permisos de administrador a este usuario? Podrá acceder al panel de administración.
                        </p>
                    ) : confirmAction?.kind === 'demoteAdmin' ? (
                        <p>¿Quitar permisos de administrador a este usuario?</p>
                    ) : confirmAction?.kind === 'deleteCapture' ? (
                        <p>¿Eliminar esta captura permanentemente?</p>
                    ) : null
                }
                confirmLabel={confirmAction?.kind === 'deleteCapture' ? 'Eliminar' : 'Confirmar'}
                onCancel={() => setConfirmAction(null)}
                onConfirm={handleConfirmDialog}
            />

            <AdminConfirmDialog
                open={!!alertMessage}
                intent="alert"
                title="Aviso"
                description={alertMessage}
                onCancel={() => setAlertMessage(null)}
                onConfirm={() => setAlertMessage(null)}
            />
        </div>
    );
}

// ─── Captures Tab ───────────────────────────────────────────────────────────

function CapturesTab({
    captures,
    capturesLoading,
    expandedCaptureId,
    setExpandedCaptureId,
    editingCommentId,
    setEditingCommentId,
    editCommentText,
    setEditCommentText,
    onDelete,
    onTogglePublic,
    onPromote,
    onSaveComment,
    updateComment,
    formatDate,
}: {
    captures: AdminCapture[];
    capturesLoading: boolean;
    expandedCaptureId: string | null;
    setExpandedCaptureId: (id: string | null) => void;
    editingCommentId: string | null;
    setEditingCommentId: (id: string | null) => void;
    editCommentText: string;
    setEditCommentText: (t: string) => void;
    onDelete: (id: string) => void;
    onTogglePublic: (c: AdminCapture) => Promise<void>;
    onPromote: (id: string) => Promise<void>;
    onSaveComment: () => Promise<void>;
    updateComment: { mutateAsync: (p: { commentId: string; text: string }) => Promise<void>; isPending: boolean };
    formatDate: (d?: string | null) => string;
}) {
    return (
        <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Fish size={18} className="text-muted-foreground" />
                Capturas del usuario
            </h3>
            {capturesLoading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            ) : captures.length === 0 ? (
                <p className="text-muted-foreground text-center py-12">Sin capturas</p>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {captures.map((c) => (
                        <CaptureCard
                            key={c.id}
                            capture={c}
                            isExpanded={expandedCaptureId === c.id}
                            onExpand={() => setExpandedCaptureId(expandedCaptureId === c.id ? null : c.id)}
                            editingCommentId={editingCommentId}
                            setEditingCommentId={setEditingCommentId}
                            editCommentText={editCommentText}
                            setEditCommentText={setEditCommentText}
                            onDelete={onDelete}
                            onTogglePublic={onTogglePublic}
                            onPromote={onPromote}
                            onSaveComment={onSaveComment}
                            updateComment={updateComment}
                            formatDate={formatDate}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function CaptureCard({
    capture,
    isExpanded,
    onExpand,
    editingCommentId,
    setEditingCommentId,
    editCommentText,
    setEditCommentText,
    onDelete,
    onTogglePublic,
    onPromote,
    onSaveComment,
    updateComment,
    formatDate,
}: {
    capture: AdminCapture;
    isExpanded: boolean;
    onExpand: () => void;
    editingCommentId: string | null;
    setEditingCommentId: (id: string | null) => void;
    editCommentText: string;
    setEditCommentText: (t: string) => void;
    onDelete: (id: string) => void;
    onTogglePublic: (c: AdminCapture) => Promise<void>;
    onPromote: (id: string) => Promise<void>;
    onSaveComment: () => Promise<void>;
    updateComment: { mutateAsync: (p: { commentId: string; text: string }) => Promise<void>; isPending: boolean };
    formatDate: (d?: string | null) => string;
}) {
    const [imgError, setImgError] = useState(false);
    const imageUrl = capture.image_url ? `${MEDIA_BASE}/${capture.image_url}` : null;
    const { data: comments } = useAdminCaptureComments(isExpanded ? capture.id : null);
    const showPlaceholder = !imageUrl || imgError;

    return (
        <div className="border border-border rounded-xl overflow-hidden">
            <div
                className="flex gap-4 p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                onClick={onExpand}
            >
                <div className="w-20 h-20 rounded-lg bg-muted shrink-0 overflow-hidden flex-shrink-0">
                    {showPlaceholder ? (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                            <Fish size={24} />
                        </div>
                    ) : (
                        <img
                            src={imageUrl}
                            alt={capture.species || ''}
                            className="w-full h-full object-cover"
                            onError={() => setImgError(true)}
                            referrerPolicy="no-referrer"
                        />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground">{capture.species || 'Sin especie'}</p>
                    <p className="text-sm text-muted-foreground truncate">{capture.description || '—'}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                        <span className={cn(
                            "text-xs px-2 py-0.5 rounded",
                            capture.is_public ? "bg-green-500/20 text-green-700" : "bg-muted text-muted-foreground"
                        )}>
                            {capture.is_public ? 'Pública' : 'Privada'}
                        </span>
                        {capture.promoted_at && (
                            <span className="text-xs px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-700 flex items-center gap-1">
                                <Star size={10} /> Promocionada
                            </span>
                        )}
                        <span className="text-xs text-muted-foreground">👍 {capture.likes_count} · 👎 {capture.dislikes_count} · 💬 {capture.comments_count}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{formatDate(capture.created_at)}</p>
                </div>
            </div>

            {isExpanded && (
                <div className="px-4 pb-4 pt-0 border-t border-border" onClick={(e) => e.stopPropagation()}>
                    <div className="flex flex-wrap gap-2 mt-4">
                        <button
                            onClick={() => onTogglePublic(capture)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-muted/50 hover:bg-muted text-sm font-medium"
                        >
                            {capture.is_public ? <GlobeLock size={14} /> : <Globe size={14} />}
                            {capture.is_public ? 'Hacer privada' : 'Hacer pública'}
                        </button>
                        <button
                            onClick={() => onPromote(capture.id)}
                            disabled={!!capture.promoted_at}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-muted/50 hover:bg-muted text-sm font-medium disabled:opacity-50"
                        >
                            <Star size={14} />
                            {capture.promoted_at ? 'Ya promocionada' : 'Promocionar al feed'}
                        </button>
                        <button
                            onClick={() => onDelete(capture.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-destructive/50 bg-destructive/10 hover:bg-destructive/20 text-destructive text-sm font-medium"
                        >
                            <Trash2 size={14} />
                            Eliminar
                        </button>
                    </div>

                    {comments && comments.length > 0 && (
                        <div className="mt-4">
                            <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                <MessageSquare size={14} />
                                Comentarios
                            </h4>
                            <div className="space-y-2">
                                {comments.map((cm) => (
                                    <CommentRow
                                        key={cm.id}
                                        comment={cm}
                                        editingCommentId={editingCommentId}
                                        editCommentText={editCommentText}
                                        setEditingCommentId={setEditingCommentId}
                                        setEditCommentText={setEditCommentText}
                                        onSave={onSaveComment}
                                        isSaving={updateComment.isPending}
                                        formatDate={formatDate}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function CommentRow({
    comment,
    editingCommentId,
    editCommentText,
    setEditingCommentId,
    setEditCommentText,
    onSave,
    isSaving,
    formatDate,
}: {
    comment: AdminCaptureComment;
    editingCommentId: string | null;
    editCommentText: string;
    setEditingCommentId: (id: string | null) => void;
    setEditCommentText: (t: string) => void;
    onSave: () => Promise<void>;
    isSaving: boolean;
    formatDate: (d?: string | null) => string;
}) {
    const isEditing = editingCommentId === comment.id;

    return (
        <div className="p-3 bg-muted/20 rounded-lg text-sm">
            <div className="flex justify-between items-start gap-2">
                <div className="flex-1 min-w-0">
                    {isEditing ? (
                        <div className="space-y-2">
                            <textarea
                                value={editCommentText}
                                onChange={(e) => setEditCommentText(e.target.value)}
                                className="w-full p-2 rounded border border-border bg-background text-foreground text-sm resize-none"
                                rows={2}
                            />
                            <div className="flex gap-2">
                                <button
                                    onClick={onSave}
                                    disabled={isSaving}
                                    className="px-2 py-1 rounded bg-primary text-primary-foreground text-xs font-medium disabled:opacity-50"
                                >
                                    Guardar
                                </button>
                                <button
                                    onClick={() => { setEditingCommentId(null); setEditCommentText(''); }}
                                    className="px-2 py-1 rounded bg-muted text-foreground text-xs font-medium"
                                >
                                    <X size={12} />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <p className="text-foreground">{comment.text}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                                {comment.author_name} · {formatDate(comment.created_at)}
                            </p>
                            <button
                                onClick={() => { setEditingCommentId(comment.id); setEditCommentText(comment.text); }}
                                className="mt-2 flex items-center gap-1 text-xs text-primary hover:underline"
                            >
                                <Edit2 size={12} />
                                Editar
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── Subcomponents ──────────────────────────────────────────────────────────

function KpiCard({ icon, label, value, subvalue }: { icon: React.ReactNode; label: string; value: string | number; subvalue?: string }) {
    return (
        <div className="bg-card border border-border rounded-xl p-4 flex items-start gap-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="p-2.5 bg-muted/30 rounded-lg border border-border/50">{icon}</div>
            <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</p>
                <p className="text-2xl font-bold text-foreground mt-0.5">{value}</p>
                {subvalue && <p className="text-xs text-muted-foreground">{subvalue}</p>}
            </div>
        </div>
    );
}

function StatBar({ label, value, color }: { label: string; value: number; color: string }) {
    return (
        <div>
            <div className="flex justify-between text-xs mb-1">
                <span className="font-medium text-foreground">{label}</span>
                <span className="text-muted-foreground">{value}/100</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className={cn("h-full rounded-full", color)} style={{ width: `${Math.min(value, 100)}%` }} />
            </div>
        </div>
    );
}

function DetailRow({ label, value, copyable }: { label: string; value: string; copyable?: boolean }) {
    const handleCopy = () => {
        if (copyable) navigator.clipboard.writeText(value);
    };
    return (
        <div className="flex justify-between items-center py-2 border-b border-muted/50 last:border-0 last:pb-0">
            <span className="text-sm text-muted-foreground font-medium">{label}</span>
            <span
                onClick={handleCopy}
                className={cn(
                    "text-sm text-foreground max-w-[200px] truncate",
                    copyable && "cursor-pointer hover:text-primary transition-colors font-mono bg-muted/30 px-1.5 py-0.5 rounded"
                )}
                title={copyable ? "Clic para copiar" : undefined}
            >
                {value}
            </span>
        </div>
    );
}

function TabButton({ active, onClick, label, icon }: { active: boolean; onClick: () => void; label: string; icon: React.ReactNode }) {
    return (
        <button
            onClick={onClick}
            className={cn(
                "flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors hover:bg-muted/50",
                active ? "border-primary text-primary bg-muted/10" : "border-transparent text-muted-foreground hover:text-foreground"
            )}
        >
            {icon}
            {label}
        </button>
    );
}

function ActivityIcon({ type }: { type: string }) {
    if (type.includes('session')) return <Smartphone size={14} />;
    if (type.includes('capture')) return <Target size={14} />;
    if (type.includes('battle')) return <Shield size={14} />;
    if (type.includes('quiz')) return <GraduationCap size={14} />;
    if (type.includes('share')) return <Share2 size={14} />;
    return <Activity size={14} />;
}

function formatEventType(type: string) {
    const map: Record<string, string> = {
        'session_start': 'Inicio de Sesión',
        'capture_created': 'Captura Registrada',
        'battle_finished': 'Batalla Finalizada',
        'quiz_completed': 'Quiz Completado',
        'share_capture': 'Captura Compartida',
        'page_view': 'Visualización de Pantalla',
    };
    return map[type] || type.replace(/_/g, ' ');
}
